import { track } from '@eventbrite/datalayer-library';
import { sdkRequest } from '@eventbrite/http';
import { checkGate } from '@eventbrite/statsig';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { formatUrl } from 'url-lib';

export const FOLLOWER_ADS_FEATURE_GATE = 'eb_ads_follower_ads_gate';

interface FollowerAdsEvent {
    isPromoted?: boolean;
    organizerName?: string;
    organizerId?: string;
    promotedListingMetadata?: {
        includeFollowCta?: boolean;
    };
}

export const useFollowerAdsEnabled = (event: FollowerAdsEvent) =>
    useMemo(
        () =>
            checkGate(FOLLOWER_ADS_FEATURE_GATE) &&
            !!event.isPromoted &&
            !!event.organizerName &&
            !!event.organizerId &&
            !!event.promotedListingMetadata?.includeFollowCta,
        [
            event.isPromoted,
            event.organizerName,
            event.organizerId,
            event.promotedListingMetadata?.includeFollowCta,
        ],
    );

export const DISCOVER_SEARCH_PAGE_AREA = 'discovery-search';

export const DISCOVER_FOLLOW_BUTTON_VIEW =
    'Discovery Search - Follower Ads - View - Follow Button';
export const DISCOVER_FOLLOW_CLICK =
    'Discovery Search - Follower Ads - Click - Follow Organizer';
export const DISCOVER_UNFOLLOW_CLICK =
    'Discovery Search - Follower Ads - Click - Unfollow';
export const DISCOVER_FOLLOW_SUCCESS =
    'Discovery Search - Follower Ads - Success - Follow Organizer';
export const DISCOVER_UNFOLLOW_SUCCESS =
    'Discovery Search - Follower Ads - Unfollow - Success';

interface UseOrganizerFollowOptions {
    organizerId?: string;
    isAuthenticated?: boolean;
}

interface FollowStatusResponse {
    follow_status: {
        followed_by_you: boolean;
        num_followers: number;
    };
}

export const useOrganizerFollow = ({
    organizerId,
    isAuthenticated,
}: UseOrganizerFollowOptions) => {
    const [isFollowing, setIsFollowing] = useState(false);

    useEffect(() => {
        if (!organizerId || !isAuthenticated) return;

        sdkRequest(
            formatUrl(`/api/v3/organizers/${organizerId}/`, {
                'expand.organizer': 'follow_status',
            }),
        )
            .then((response: FollowStatusResponse) => {
                setIsFollowing(response.follow_status.followed_by_you);
            })
            .catch(() => {
                // Silently fail — default remains false
            });
    }, [organizerId, isAuthenticated]);

    const trackFollowButtonView = useCallback(() => {
        if (!organizerId) return;

        track({
            eventName: DISCOVER_FOLLOW_BUTTON_VIEW,
            eventData: {
                organizerId,
                pageArea: DISCOVER_SEARCH_PAGE_AREA,
            },
        });
    }, [organizerId]);

    const toggleFollow = useCallback(() => {
        if (!organizerId) return;

        const clickEvent = isFollowing
            ? DISCOVER_UNFOLLOW_CLICK
            : DISCOVER_FOLLOW_CLICK;

        track({
            eventName: clickEvent,
            eventData: {
                organizerId,
                pageArea: DISCOVER_SEARCH_PAGE_AREA,
                // signedIn already exists on the eventData object
            },
        });

        if (!isAuthenticated) {
            const signInUrl = `/signin/?referrer=${encodeURIComponent(
                window.location.href,
            )}`;
            window.location.assign(signInUrl);
            return;
        }

        const endpoint = isFollowing
            ? `/api/v3/destination/organizers/${organizerId}/unfollow/`
            : `/api/v3/destination/organizers/${organizerId}/follow/`;

        sdkRequest(endpoint, { method: 'POST' })
            .then(() => {
                setIsFollowing((prev) => !prev);

                const successEvent = isFollowing
                    ? DISCOVER_UNFOLLOW_SUCCESS
                    : DISCOVER_FOLLOW_SUCCESS;

                track({
                    eventName: successEvent,
                    eventData: {
                        organizerId,
                        pageArea: DISCOVER_SEARCH_PAGE_AREA,
                    },
                });
            })
            .catch(() => {
                // Silently fail
            });
    }, [organizerId, isAuthenticated, isFollowing]);

    return { isFollowing, toggleFollow, trackFollowButtonView };
};
